import { Component, OnInit, afterNextRender } from '@angular/core';
import { FeriasBaseComponent } from '../../ferias-base.component';
import { Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { AlertMessageService } from '@sigepe/sigepe-template';
import { SituacaoParcelaFeriasEnum } from 'src/app/components/shared/enums/situacao-parcela-ferias.enum';
import { ExercicioSelecionado, ExerciciosService, ParcelaFerias } from 'src/app/services/exercicios.service';
import { SituacaoFuncionalEnum } from 'src/app/components/shared/enums/situacao-funcional.enum';
import { getDateFromYYYYMMDD } from 'src/app/components/shared/date-utils';
import { Message } from 'src/app/components/shared/message';

type ExercicioSelecionadoNavegacao = ExercicioSelecionado & {
    origemTela?: string;
};

export interface ParcelaFeriasParaHomologacao {
    id?: number;
    idParcelaPai: number;
    ordem: number;
    geracao: number;
    dataInicio: Date;
    dataFim: Date;
    quantidadeDias: number;
    situacao: {
        //Vindo da enum SituacaoParcelaFeriasEnum
        descricao: string;
        codigoCor: string;
    };
    adiantamentoSalarial: {
        valor: boolean;
    };
    abonoPecuniario: {
        valor: boolean;
    };
    adiantamentoDecimoTerceiro: {
        valor: boolean;
    };
    parcelasDesconto: number;
    emAcumulo: boolean;
    emAcumuloSegundoPeriodo: boolean;
    emAcumuloTerceiroPeriodo: boolean;
}

@Component({
    selector: 'app-homologar-ferias',
    templateUrl: './homologar-ferias.component.html',
    styleUrls: ['./homologar-ferias.component.scss']
})
export class HomologarFeriasComponent extends FeriasBaseComponent implements OnInit {
    private readonly chaveEstadoConsultaGerencial = 'consultaGerencialExercicioSelecionado';
    exercicioSelecionado: ExercicioSelecionado;
    houvePreenchimento = false;
    veioDaConsultaGerencial = false;

    parcelasFerias: ParcelaFeriasParaHomologacao[];
    parcelasInterrompidas: ParcelaFeriasParaHomologacao[];
    parcelasOutras: ParcelaFeriasParaHomologacao[] = [];

    justificativaParaHomologarFerias = '';
    justificativaParaHomologarFeriasInvalida = false;

    justificativaParaAcumuloFerias = '';
    justificativaParaAcumuloFeriasInvalida = false;

    justificativaParaRecusaFerias = '';
    justificativaParaRecusaFeriasInvalida = false;

    constructor(
        private exerciciosService: ExerciciosService,
        alertaMsgService: AlertMessageService,
        private confirmationService: ConfirmationService,
        private router: Router
    ) {
        super(alertaMsgService);

        const navigationData = this.obterDadosNavegacao();
        this.exercicioSelecionado = navigationData;
        this.veioDaConsultaGerencial = navigationData?.origemTela === 'consulta-gerencial' || this.ehFluxoConsultaGerencial();

        afterNextRender(() => {
            this.configurarCabecalhoFluxo();
            this.rolarParaTopo();
        });
    }

    ngOnInit(): void {
        this.parcelasFerias = [];
        this.parcelasInterrompidas = [];

        this.exercicioSelecionado.parcelas.forEach(parcelaFerias => {
            //Parcelas Solicitadas para homologação
            const parcelaResgatada: ParcelaFeriasParaHomologacao = this.resgatarParcelaCorrente(parcelaFerias, 0);
            if (parcelaResgatada) {
                this.parcelasFerias.push(parcelaResgatada);
            }

            //Parcelas Interrompidas
            this.parcelasInterrompidas = this.parcelasInterrompidas.concat(this.resgatarParcelasInterrompidas(parcelaFerias, 0));

            //Demais parcelas
            const parcelaOutraResgatada: ParcelaFeriasParaHomologacao = this.resgatarParcelasDemaisParcelas(parcelaFerias, 0);
            if (parcelaOutraResgatada) {
                this.parcelasOutras.push(parcelaOutraResgatada);
            }
        });

        //ordenar as parcelas
        this.parcelasFerias.sort((a, b) => a.ordem - b.ordem);
    }

    get regimeCeletistaOuAnistiado(): boolean {
        return SituacaoFuncionalEnum.CLT.id === this.exercicioSelecionado.siglaRegime || SituacaoFuncionalEnum.ANISTIADO.id === this.exercicioSelecionado.siglaRegime;
    }

    get homologacaoComAcumuloFerias(): boolean {
        return !this.regimeCeletistaOuAnistiado && this.parcelasParaHomologacao.some(parcela => this.parcelaEstaEmAcumuloPeloExercicio(parcela));
    }

    get homologacaoComAcumuloFeriasNoTerceiroPeriodo(): boolean {
        return !this.regimeCeletistaOuAnistiado && this.parcelasParaHomologacao.some(parcela => parcela.emAcumuloTerceiroPeriodo);
    }

    private get parcelasParaHomologacao(): ParcelaFeriasParaHomologacao[] {
        return [...(this.parcelasFerias ?? []), ...(this.parcelasInterrompidas ?? []), ...(this.parcelasOutras ?? [])];
    }

    private parcelaEstaEmAcumuloPeloExercicio(parcela: ParcelaFeriasParaHomologacao): boolean {
        return !this.regimeCeletistaOuAnistiado && !!parcela?.dataInicio && this.obterAno(parcela.dataInicio) >= Number(this.exercicioSelecionado.anoExercicio) + 1;
    }

    private obterAno(data: Date | string): number {
        return typeof data === 'string' ? getDateFromYYYYMMDD(data).getFullYear() : data.getFullYear();
    }

    confirmarVoltar() {
        if (this.houvePreenchimento) {
            this.confirmationService.confirm({
                key: 'confirmPadrao',
                header: 'Confirmação',
                message: Message.CONFIRMACAO_ABANDONAR_TELA_COM_PERDA_DE_DADOS,
                accept: async () => {
                    this.voltar();
                }
            });
        } else {
            this.voltar();
        }
    }

    voltar(): void {
        if (this.veioDaConsultaGerencial) {
            sessionStorage.removeItem(this.chaveEstadoConsultaGerencial);
            this.router.navigate(['/secure/consulta-gerencial']);
            return;
        }
        this.router.navigate(['/secure/gerenciar-ferias/consultar-exercicios']);
    }

    private obterDadosNavegacao(): ExercicioSelecionadoNavegacao | null {
        const navigationData = this.router.getCurrentNavigation()?.extras?.state?.data as ExercicioSelecionadoNavegacao;
        if (navigationData) {
            return navigationData;
        }

        const estadoPersistido = this.recuperarEstadoConsultaGerencial();
        if (this.ehFluxoConsultaGerencial()) {
            return estadoPersistido || history.state?.data || null;
        }

        return history.state?.data || estadoPersistido || null;
    }

    abrirConfirmAcumuloFerias() {
        this.confirmationService.confirm({
            key: 'confirmAcumuloFerias',
            header: 'Confirmação',
            accept: async () => {
                this.homologarComAcumuloFerias();
            }
        });
    }

    abrirConfirmRecusar() {
        this.confirmationService.confirm({
            key: 'confirmRecusar',
            header: 'Confirmação',
            accept: async () => {
                this.recusarHomologacao();
            }
        });
    }

    confirmarHomologacao(): void {
        this.clearMessages();

        let podeHomologar = true;

        // Validação da justificativa
        this.validarJustificativaParaHomologarFerias();
        if (this.justificativaParaHomologarFeriasInvalida) {
            this.getAlertaMsgService().error(this.msgJustificativaObrigatoria);
            podeHomologar = false;
        }

        if (podeHomologar) {
            if (this.homologacaoComAcumuloFerias) {
                this.justificativaParaAcumuloFerias = '';
                this.justificativaParaAcumuloFeriasInvalida = false;
                this.abrirConfirmAcumuloFerias();
            } else {
                this.confirmationService.confirm({
                    key: 'confirmPadrao',
                    header: 'Confirmação',
                    message: 'Tem certeza que deseja homologar as férias do exercício de ' + this.exercicioSelecionado.anoExercicio + '?',
                    accept: async () => {
                        this.homologar();
                    }
                });
            }
        }
    }

    confirmarRecusa(): void {
        this.clearMessages();

        let podeRecusar = true;

        // Validação da justificativa
        this.validarJustificativaParaHomologarFerias();
        if (this.justificativaParaHomologarFeriasInvalida) {
            this.getAlertaMsgService().error(this.msgJustificativaObrigatoria);
            podeRecusar = false;
        }

        if (podeRecusar) {
            this.justificativaParaRecusaFerias = '';
            this.justificativaParaRecusaFeriasInvalida = false;
            this.abrirConfirmRecusar();
        }
    }

    homologar(): void {
        const parcelasParaHomologar: ParcelaFerias[] = [];

        const gerarParcelaFerias: Function = (parcela): ParcelaFerias => {
            return {
                id: parcela.id,
                idParcelaPai: parcela.idParcelaPai,
                ordem: parcela.ordem,
                dataInicio: parcela.dataInicio,
                dataFim: parcela.dataFim,
                quantidadeDias: parcela.quantidadeDias,
                adiantamentoSalarial: parcela.adiantamentoSalarial.valor,
                abonoPecuniario: parcela.abonoPecuniario.valor,
                adiantamentoDecimoTerceiro: parcela.adiantamentoDecimoTerceiro.valor,
                parcelasDesconto: parcela.adiantamentoSalarial.valor ? parcela.parcelasDesconto : null,
                situacao: parcela.situacao.descricao,
                justificativaAcumulo: this.parcelaEstaEmAcumuloPeloExercicio(parcela) ? this.justificativaParaAcumuloFerias : null
            };
        };

        this.parcelasOutras.forEach(parcela => {
            parcelasParaHomologar.push(gerarParcelaFerias(parcela));
        });

        this.parcelasInterrompidas.forEach(parcela => {
            parcelasParaHomologar.push(gerarParcelaFerias(parcela));
        });

        this.parcelasFerias.forEach(parcela => {
            parcelasParaHomologar.push(gerarParcelaFerias(parcela));
        });

        const homologarFerias: any = {
            parcelas: parcelasParaHomologar,
            justificativaParaHomologarFerias: this.justificativaParaHomologarFerias
        };

        this.exerciciosService.homologarFerias(this.exercicioSelecionado.idFerias, homologarFerias).subscribe(data => {
            if (data.sucessoEnvioMensageria) {
                this.getAlertaMsgService().setAwatingSuccessMessage(Message.FERIAS_HOMOLOGADAS_COM_SUCESSO);
            } else {
                this.getAlertaMsgService().setAwatingWarnMessage(Message.AVISO_ACAO_REALIZADA_SEM_NOTIFICACAO_AUTOMATICA);
            }
            if (this.veioDaConsultaGerencial) {
                sessionStorage.setItem('consultaGerencialAtualizarAposAcao', 'true');
            }
            this.voltar();
        });
    }

    homologarComAcumuloFerias(): void {
        let podeHomologar = true;

        // Validação da justificativa
        this.validarJustificativaParaAcumuloFerias();
        if (this.justificativaParaAcumuloFeriasInvalida) {
            podeHomologar = false;
        }

        if (!podeHomologar) {
            setTimeout(() => {
                this.abrirConfirmAcumuloFerias();
            }, 200);
        } else {
            this.homologar();
        }
    }

    recusarHomologacao(): void {
        let podeRecusar = true;

        // Validação da justificativa
        this.validarJustificativaParaRecusaFerias();
        if (this.justificativaParaRecusaFeriasInvalida) {
            podeRecusar = false;
        }

        if (!podeRecusar) {
            setTimeout(() => {
                this.abrirConfirmRecusar();
            }, 200);
        } else {
            const parcelasProgramadas: ParcelaFerias[] = [];

            this.parcelasFerias.forEach(parcela => {
                parcelasProgramadas.push({
                    id: parcela.id,
                    ordem: parcela.ordem,
                    dataInicio: parcela.dataInicio,
                    quantidadeDias: parcela.quantidadeDias,
                    adiantamentoSalarial: parcela.adiantamentoSalarial.valor,
                    abonoPecuniario: parcela.abonoPecuniario.valor,
                    adiantamentoDecimoTerceiro: parcela.adiantamentoDecimoTerceiro.valor,
                    parcelasDesconto: parcela.adiantamentoSalarial.valor ? parcela.parcelasDesconto : null
                });
            });

            const recusarFerias: any = {
                parcelas: parcelasProgramadas,
                justificativaParaHomologarFerias: this.justificativaParaHomologarFerias,
                justificativaParaRecusaFerias: this.justificativaParaRecusaFerias
            };

            this.exerciciosService.recusarFerias(this.exercicioSelecionado.idFerias, recusarFerias).subscribe(data => {
                if (data.sucessoEnvioMensageria) {
                    this.getAlertaMsgService().setAwatingSuccessMessage(Message.FERIAS_RECUSADAS_COM_SUCESSO);
                } else {
                    this.getAlertaMsgService().setAwatingWarnMessage(Message.AVISO_ACAO_REALIZADA_SEM_NOTIFICACAO_AUTOMATICA);
                }
                if (this.veioDaConsultaGerencial) {
                    sessionStorage.setItem('consultaGerencialAtualizarAposAcao', 'true');
                }
                this.voltar();
            });
        }
    }

    get msgJustificativaRecusaFeriasObrigatoria(): string {
        return Message.ERRO_JUSTIFICATIVA_RECUSA_FERIAS_OBRIGATORIA;
    }

    get msgJustificativaAcumuloFeriasObrigatoria(): string {
        return Message.ERRO_JUSTIFICATIVA_ACUMULO_FERIAS_OBRIGATORIA;
    }

    get msgJustificativaObrigatoria(): string {
        return Message.ERRO_JUSTIFICATIVA_OBRIGATORIA;
    }

    validarJustificativaParaRecusaFerias() {
        if (!this.justificativaParaRecusaFerias || this.justificativaParaRecusaFerias.trim().length < 10 || this.justificativaParaRecusaFerias.trim().length > 300) {
            this.justificativaParaRecusaFeriasInvalida = true;
        } else {
            this.justificativaParaRecusaFeriasInvalida = false;
        }
    }

    validarJustificativaParaAcumuloFerias() {
        if (!this.justificativaParaAcumuloFerias || this.justificativaParaAcumuloFerias.trim().length < 10 || this.justificativaParaAcumuloFerias.trim().length > 300) {
            this.justificativaParaAcumuloFeriasInvalida = true;
        } else {
            this.justificativaParaAcumuloFeriasInvalida = false;
        }
    }

    validarJustificativaParaHomologarFerias() {
        if (!this.justificativaParaHomologarFerias || this.justificativaParaHomologarFerias.trim().length < 10 || this.justificativaParaHomologarFerias.trim().length > 300) {
            this.justificativaParaHomologarFeriasInvalida = true;
        } else {
            this.justificativaParaHomologarFeriasInvalida = false;
        }
    }

    onJustificativaParaHomologarFeriasChange(event) {
        this.houvePreenchimento = true;
        this.justificativaParaHomologarFerias = event.target.value ? event.target.value.trim() : '';
    }

    private recuperarEstadoConsultaGerencial(): ExercicioSelecionado | null {
        try {
            const estadoSerializado = sessionStorage.getItem(this.chaveEstadoConsultaGerencial);
            return estadoSerializado ? JSON.parse(estadoSerializado) : null;
        } catch {
            return null;
        }
    }

    private ehFluxoConsultaGerencial(): boolean {
        return this.router.url.includes('/secure/consulta-gerencial/');
    }

    private configurarCabecalhoFluxo(): void {
        if (this.veioDaConsultaGerencial) {
            this.alterarTituloTemplate('Consulta Gerencial');
            this.alterarTextoBreadcrumb(1, 'Consulta Gerencial');
            return;
        }

        this.alterarTituloTemplate('Gerenciar Férias');
        this.alterarTextoBreadcrumb(1, 'Gerenciar Férias - Buscar Agente Público');
    }

    onJustificativaParaAcumuloFeriasChange(event) {
        this.houvePreenchimento = true;
        this.justificativaParaAcumuloFerias = event.target.value ? event.target.value.trim() : '';
    }

    onJustificativaParaRecusaFeriasChange(event) {
        this.houvePreenchimento = true;
        this.justificativaParaRecusaFerias = event.target.value ? event.target.value.trim() : '';
    }

    resgatarParcelasDemaisParcelas(parcelaFerias: any, profundidade: number): ParcelaFeriasParaHomologacao {
        if (!parcelaFerias.parcelaFilha) {
            //Só exibe as parcelas na situação Solicitada
            if (SituacaoParcelaFeriasEnum.fromDesc(parcelaFerias.situacao) !== SituacaoParcelaFeriasEnum.SOLICITADA && SituacaoParcelaFeriasEnum.fromDesc(parcelaFerias.situacao) !== SituacaoParcelaFeriasEnum.INTERROMPIDA) {
                return {
                    id: parcelaFerias.id,
                    idParcelaPai: parcelaFerias.idParcelaPai,
                    ordem: parcelaFerias.ordem,
                    geracao: profundidade,
                    dataInicio: parcelaFerias.dataInicio,
                    dataFim: parcelaFerias.dataFim,
                    quantidadeDias: parcelaFerias.quantidadeDias,
                    situacao: {
                        descricao: SituacaoParcelaFeriasEnum.fromDesc(parcelaFerias.situacao).descricao,
                        codigoCor: SituacaoParcelaFeriasEnum.fromDesc(parcelaFerias.situacao).extras.codigoCor
                    },
                    adiantamentoSalarial: {
                        valor: parcelaFerias.adiantamentoSalarial
                    },
                    abonoPecuniario: {
                        valor: parcelaFerias.abonoPecuniario
                    },
                    adiantamentoDecimoTerceiro: {
                        valor: parcelaFerias.adiantamentoDecimoTerceiro
                    },
                    parcelasDesconto: parcelaFerias.parcelasDesconto,
                    emAcumulo: parcelaFerias.emAcumulo ? true : false,
                    emAcumuloSegundoPeriodo: parcelaFerias.dataInicio && getDateFromYYYYMMDD(parcelaFerias.dataInicio).getFullYear() === getDateFromYYYYMMDD(this.exercicioSelecionado.dataFimPeriodoProgramacao).getFullYear(),
                    emAcumuloTerceiroPeriodo: parcelaFerias.dataInicio && getDateFromYYYYMMDD(parcelaFerias.dataInicio).getFullYear() === getDateFromYYYYMMDD(this.exercicioSelecionado.dataFimPeriodoProgramacao).getFullYear() + 1
                };
            } else {
                return undefined;
            }
        } else {
            return this.resgatarParcelasDemaisParcelas(parcelaFerias.parcelaFilha, profundidade + 1);
        }
    }

    resgatarParcelaCorrente(parcelaFerias: any, profundidade: number): ParcelaFeriasParaHomologacao {
        if (!parcelaFerias.parcelaFilha) {
            //Só exibe as parcelas na situação Solicitada
            if (SituacaoParcelaFeriasEnum.fromDesc(parcelaFerias.situacao) == SituacaoParcelaFeriasEnum.SOLICITADA) {
                return {
                    id: parcelaFerias.id,
                    idParcelaPai: parcelaFerias.idParcelaPai,
                    ordem: parcelaFerias.ordem,
                    geracao: profundidade,
                    dataInicio: parcelaFerias.dataInicio,
                    dataFim: parcelaFerias.dataFim,
                    quantidadeDias: parcelaFerias.quantidadeDias,
                    situacao: {
                        descricao: SituacaoParcelaFeriasEnum.fromDesc(parcelaFerias.situacao).descricao,
                        codigoCor: SituacaoParcelaFeriasEnum.fromDesc(parcelaFerias.situacao).extras.codigoCor
                    },
                    adiantamentoSalarial: {
                        valor: parcelaFerias.adiantamentoSalarial
                    },
                    abonoPecuniario: {
                        valor: parcelaFerias.abonoPecuniario
                    },
                    adiantamentoDecimoTerceiro: {
                        valor: parcelaFerias.adiantamentoDecimoTerceiro
                    },
                    parcelasDesconto: parcelaFerias.parcelasDesconto,
                    emAcumulo: parcelaFerias.emAcumulo ? true : false,
                    emAcumuloSegundoPeriodo: parcelaFerias.dataInicio && getDateFromYYYYMMDD(parcelaFerias.dataInicio).getFullYear() === getDateFromYYYYMMDD(this.exercicioSelecionado.dataFimPeriodoProgramacao).getFullYear(),
                    emAcumuloTerceiroPeriodo: parcelaFerias.dataInicio && getDateFromYYYYMMDD(parcelaFerias.dataInicio).getFullYear() === getDateFromYYYYMMDD(this.exercicioSelecionado.dataFimPeriodoProgramacao).getFullYear() + 1
                };
            } else {
                return undefined;
            }
        } else {
            return this.resgatarParcelaCorrente(parcelaFerias.parcelaFilha, profundidade + 1);
        }
    }

    resgatarParcelasInterrompidas(parcelaFerias: any, profundidade: number): ParcelaFeriasParaHomologacao[] {
        let parcelasInterrompidas: ParcelaFeriasParaHomologacao[] = [];

        if (SituacaoParcelaFeriasEnum.fromDesc(parcelaFerias.situacao) == SituacaoParcelaFeriasEnum.INTERROMPIDA) {
            parcelasInterrompidas.push({
                id: parcelaFerias.id,
                idParcelaPai: parcelaFerias.idParcelaPai,
                ordem: parcelaFerias.ordem,
                geracao: profundidade,
                dataInicio: parcelaFerias.dataInicio,
                dataFim: parcelaFerias.dataFim,
                quantidadeDias: parcelaFerias.quantidadeDias,
                situacao: {
                    descricao: SituacaoParcelaFeriasEnum.fromDesc(parcelaFerias.situacao).descricao,
                    codigoCor: SituacaoParcelaFeriasEnum.fromDesc(parcelaFerias.situacao).extras.codigoCor
                },
                adiantamentoSalarial: {
                    valor: parcelaFerias.adiantamentoSalarial
                },
                abonoPecuniario: {
                    valor: parcelaFerias.abonoPecuniario
                },
                adiantamentoDecimoTerceiro: {
                    valor: parcelaFerias.adiantamentoDecimoTerceiro
                },
                parcelasDesconto: parcelaFerias.parcelasDesconto,
                emAcumulo: parcelaFerias.emAcumulo ? true : false,
                emAcumuloSegundoPeriodo: parcelaFerias.dataInicio && getDateFromYYYYMMDD(parcelaFerias.dataInicio).getFullYear() === getDateFromYYYYMMDD(this.exercicioSelecionado.dataFimPeriodoProgramacao).getFullYear(),
                emAcumuloTerceiroPeriodo: parcelaFerias.dataInicio && getDateFromYYYYMMDD(parcelaFerias.dataInicio).getFullYear() === getDateFromYYYYMMDD(this.exercicioSelecionado.dataFimPeriodoProgramacao).getFullYear() + 1
            });
        }

        if (parcelaFerias.parcelaFilha) {
            parcelasInterrompidas = parcelasInterrompidas.concat(this.resgatarParcelasInterrompidas(parcelaFerias.parcelaFilha, profundidade + 1));
        }

        return parcelasInterrompidas;
    }
}
